import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, ExternalLink, Share2, Store, Globe, FileJson, Network, Braces } from "lucide-react";
import { API } from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";

const ORIGIN = API.replace(/\/api$/, "");

function CopyRow({ label, url, icon: Icon, testid }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success(t("share.copied"));
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">{url}</p>
      </div>
      <button onClick={copy} data-testid={`${testid}-copy`} className="rounded-md border border-border p-2 transition-colors hover:bg-secondary" title={t("share.copy")}>
        {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
      </button>
      <a href={url} target="_blank" rel="noreferrer" data-testid={`${testid}-open`} className="rounded-md border border-border p-2 transition-colors hover:bg-secondary" title={t("share.open")}>
        <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function AIShare() {
  const t = useT();
  const { store, loading } = useStore();

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Store className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
        <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
      </div>
    );
  }

  const publicUrl = `${ORIGIN}/store/${store.id}`;
  const base = `${API}/public/stores/${store.id}`;
  const endpoints = [
    { label: t("share.productsJson"), url: `${base}/feed/products.json`, icon: FileJson, testid: "endpoint-json" },
    { label: t("share.productsJsonld"), url: `${base}/feed/products.jsonld`, icon: Braces, testid: "endpoint-jsonld" },
    { label: t("share.knowledge"), url: `${base}/knowledge`, icon: Network, testid: "endpoint-knowledge" },
    { label: t("share.schema"), url: `${base}/schema`, icon: FileJson, testid: "endpoint-schema" },
  ];

  return (
    <div className="max-w-3xl">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
        <Share2 className="h-6 w-6 text-brand" />{t("share.title")}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("share.subtitle")}</p>

      {store.status !== "published" && (
        <div className="mt-6 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">{t("share.publishFirst")}</div>
      )}

      <section className="mt-8">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground"><Globe className="h-4 w-4 text-brand" />{t("share.publicPage")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t("share.publicPageHint")}</p>
        <div className="mt-3"><CopyRow label={store.name} url={publicUrl} icon={Globe} testid="share-public-page" /></div>
      </section>

      <section className="mt-8">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground"><FileJson className="h-4 w-4 text-brand" />{t("share.endpoints")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t("share.endpointsHint")}</p>
        <div className="mt-3 space-y-2">
          {endpoints.map((e) => <CopyRow key={e.testid} {...e} />)}
        </div>
      </section>
    </div>
  );
}
